import axios from 'axios';
import { syncEnvelopeQuotaFromApiResponse } from '../utils/envelopeQuotaSync';

// Global loading state removed — no longer showing a loader modal on API requests

/**
 * API Configuration with CORS Support
 * 
 * CORS Requirements (Backend):
 * - supports_credentials: true (allows cookies cross-origin)
 * - allowed_origins: ['http://localhost:3000', 'https://api.volksign.com']
 * - allowed_headers: ['*'] (all headers allowed)
 * - exposed_headers: ['XSRF-TOKEN'] (cookie name exposed)
 * 
 * Session Cookie Requirements (Backend):
 * - For cross-origin: SameSite='none' and Secure=true (HTTPS required)
 * - For same-site: SameSite='lax' works fine
 * 
 * Frontend Configuration:
 * - withCredentials: true (equivalent to credentials: 'include' in fetch)
 * - This allows cookies to be sent/received cross-origin
 */
const api = axios.create({
  // Point directly at backend to avoid proxy redirect issues
  // In development: http://localhost/volksign/api
  // In production: set REACT_APP_API_URL to your full backend URL (e.g. https://your-domain/volksign/api)
  baseURL: process.env.REACT_APP_API_URL || 'https://api.volksign.com/public/api',
  // Enable credentials to send/receive cookies (needed for CSRF and CORS)
  // This is equivalent to credentials: 'include' in fetch API
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Store CSRF token in memory (session-based, not persistent)
let csrfToken = null;
let tokenExpiry = null; // Cache token for 2 hours (session lifetime)
// Flag to prevent concurrent CSRF token fetches
let csrfTokenFetching = false;
// Promise to share the same CSRF fetch across concurrent requests
let csrfTokenPromise = null;

// Request deduplication - prevent duplicate identical requests within short time window
const pendingRequests = new Map();
const REQUEST_DEDUP_WINDOW = 1000; // 1 second window to deduplicate

const getRequestKey = (config) => {
  // Stable key for pending map cleanup on response (must match request and response).
  return `${config.method?.toUpperCase()}_${config.url}`;
};

/**
 * Single function to fetch CSRF token from the API
 * Uses /api/csrf-token endpoint which returns token in response body
 * Handles concurrent requests by sharing the same promise
 * Caches token for 2 hours (session lifetime)
 * @returns {Promise<string>} The CSRF token
 */
export const fetchCsrfToken = async () => {
  // If token exists and hasn't expired, return it
  if (csrfToken && tokenExpiry && Date.now() < tokenExpiry) {
    return csrfToken;
  }
  
  // If already fetching, wait for the existing promise
  if (csrfTokenFetching && csrfTokenPromise) {
    await csrfTokenPromise;
    return csrfToken;
  }
  
  // Start new fetch
  csrfTokenFetching = true;
  csrfTokenPromise = (async () => {
    try {
      // Call /api/csrf-token endpoint which returns token in response body
      // This works for cross-origin requests (unlike cookies)
      // Use baseURL to construct the full URL
      const baseURL = process.env.REACT_APP_API_URL || 'https://api.volksign.com/public/api';
      const csrfTokenUrl = `${baseURL}/csrf-token`;
      
      console.log('🔄 Fetching CSRF token from:', csrfTokenUrl);
      
      const csrfResponse = await axios.get(csrfTokenUrl, {
        withCredentials: true, // Still include credentials for same-origin scenarios
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        }
      });
      
      console.log('📥 CSRF token response received:', {
        status: csrfResponse.status,
        statusText: csrfResponse.statusText,
        data: csrfResponse.data,
        headers: csrfResponse.headers,
      });
      
      // Extract token from response body
      // The token is in response.data.csrf_token
      let tokenFromResponse = null;
      
      if (csrfResponse.data) {
        // Try different possible response formats
        tokenFromResponse = 
          csrfResponse.data.csrf_token || 
          csrfResponse.data.csrfToken || 
          csrfResponse.data.token ||
          (typeof csrfResponse.data === 'string' ? csrfResponse.data : null); 
      }
      
      if (tokenFromResponse) {
        csrfToken = tokenFromResponse;
        
        // Use expiresAt from API response if available, otherwise default to 2 hours
        if (csrfResponse.data?.expiresAt) {
          tokenExpiry = new Date(csrfResponse.data.expiresAt).getTime();
        } else if (csrfResponse.data?.expires_at) {
          tokenExpiry = new Date(csrfResponse.data.expires_at).getTime();
        } else {
          // Fallback: Cache token for 2 hours (session lifetime)
          tokenExpiry = Date.now() + (2 * 60 * 60 * 1000);
        }
        
        console.log('✅ CSRF token fetched successfully:', {
          token: csrfToken.substring(0, 30) + '...',
          length: csrfToken.length,
          expiresAt: new Date(tokenExpiry).toISOString(),
          expiresIn: Math.round((tokenExpiry - Date.now()) / 1000 / 60) + ' minutes',
        });
        return csrfToken;
      } else {
        console.error('❌ CSRF token not found in response body. Full response:', {
          data: csrfResponse.data,
          dataType: typeof csrfResponse.data,
          dataKeys: csrfResponse.data ? Object.keys(csrfResponse.data) : 'N/A',
        });
        throw new Error('CSRF token not found in response body');
      }
    } catch (csrfError) {
      console.error('Failed to fetch CSRF token:', csrfError.message);
      // Reset flags on error so it can be retried
      csrfTokenFetching = false;
      csrfTokenPromise = null;
      throw new Error('CSRF token fetch failed. Please try again.');
    } finally {
      // Only reset if not already reset in catch block
      if (csrfTokenFetching) {
        csrfTokenFetching = false;
        csrfTokenPromise = null;
      }
    }
  })();
  
  await csrfTokenPromise;
  return csrfToken;
};

// Request interceptor - Add Bearer token and CSRF token
api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Skip CSRF for GET requests and CSRF token endpoint
  const isGetRequest = config.method === 'get' || (config.url && config.url.includes('/csrf-token'));
  
  // Declare requestKey outside the block so it's available in error handler
  let requestKey = null;
  
  if (isGetRequest) {
    return config;
  }

  // Multipart uploads reuse the same URL with different bodies (e.g. batched files).
  // Some JSON endpoints also intentionally fire multiple same-URL requests with different payloads.
  // Do not dedupe these: method+URL-only keys would cancel valid requests as DUPLICATE_REQUEST_CANCELLED.
  const url = String(config.url || '');
  const skipDedupByUrl = (
    url.includes('/document-files-batch') ||
    url.includes('/document-files-batch-zip')
  );
  const skipDedup = config.data instanceof FormData || config.skipRequestDedup === true || skipDedupByUrl;

  if (!skipDedup) {
    // Check for duplicate requests within deduplication window (for POST/PUT/DELETE/PATCH)
    requestKey = getRequestKey(config);
    const now = Date.now();

    if (pendingRequests.has(requestKey)) {
      const { timestamp } = pendingRequests.get(requestKey);
      const timeSince = now - timestamp;

      if (timeSince < REQUEST_DEDUP_WINDOW) {
        console.log('🔄 Duplicate request detected within window, cancelling:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          timeSince: `${timeSince}ms`,
          window: `${REQUEST_DEDUP_WINDOW}ms`
        });
        const abortController = new AbortController();
        abortController.abort();
        config.signal = abortController.signal;
        return Promise.reject(new Error('DUPLICATE_REQUEST_CANCELLED'));
      }
      pendingRequests.delete(requestKey);
    }

    pendingRequests.set(requestKey, {
      timestamp: now
    });
  }
  
  // Fetch CSRF token if not present or expired (needed for Laravel Sanctum)
  // Don't proceed with request until CSRF token is fetched
  if (!csrfToken || (tokenExpiry && Date.now() >= tokenExpiry)) {
    if (csrfToken && tokenExpiry && Date.now() >= tokenExpiry) {
      console.log('⏰ CSRF token expired, fetching new one...');
      csrfToken = null; // Clear expired token
    }
    try {
      await fetchCsrfToken();
    } catch (error) {
      // Clean up pending request tracking on error
      if (requestKey) {
        pendingRequests.delete(requestKey);
      }
      return Promise.reject(error);
    }
  }
  
  // Add CSRF token for stateful requests (POST, PUT, DELETE, PATCH)
  // Token is sent in X-XSRF-TOKEN header (as per guide)
  if (csrfToken) {
    // Send token in X-XSRF-TOKEN header (standard Laravel Sanctum approach)
    config.headers['X-XSRF-TOKEN'] = csrfToken;
    console.log('✅ Adding X-XSRF-TOKEN header to request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      headerValue: csrfToken.substring(0, 30) + '...',
      allHeaders: Object.keys(config.headers),
    });
  } else {
    console.warn('⚠️ No CSRF token available for request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
    });
  }
  
  return config;
});

// Response interceptor - Handle errors including CORS and 401 unauthorized, track loading state
api.interceptors.response.use(
  (response) => {
    // Clean up pending request tracking on success
    const requestKey = getRequestKey(response.config);
    if (pendingRequests.has(requestKey)) {
      pendingRequests.delete(requestKey);
    }

    try {
      syncEnvelopeQuotaFromApiResponse(response?.data);
    } catch {
      /* quota sync must not break API responses */
    }
    
    return response;
  },
  (error) => {
    // Handle duplicate request cancellation (not a real error)
    if (error.message === 'DUPLICATE_REQUEST_CANCELLED') {
      console.log('✅ Duplicate request cancelled successfully');
      return Promise.reject(error);
    }
    
    // Clean up pending request tracking on error
    if (error.config) {
      const requestKey = getRequestKey(error.config);
      if (pendingRequests.has(requestKey)) {
        pendingRequests.delete(requestKey);
      }
    }
    // Handle CORS errors
    if (!error.response && error.message) {
      // Network error or CORS error (no response received)
      if (error.message.includes('CORS') || error.message.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.error('CORS Error: Check backend CORS configuration');
        console.error('Ensure:', {
          'allowed_origins': 'Includes your frontend origin (http://localhost:3000)',
          'supports_credentials': 'true',
          'allowed_headers': 'Includes Content-Type, Authorization, XSRF-TOKEN',
          'allowed_methods': 'Includes POST, GET, OPTIONS',
        });
        return Promise.reject(new Error('CORS error: Unable to connect to API. Check backend CORS configuration.'));
      }
    }
    
    // Skip auth redirect for public signing routes — they use X-Signing-Token, not auth_token
    // Also never hard-navigate away from mobile embed WebViews/iframes (host app closes them).
    const pathname = window.location.pathname || '';
    const isSigningRoute = pathname.startsWith('/sign/');
    let isEmbedRoute = pathname.startsWith('/embed/');
    try {
      isEmbedRoute = isEmbedRoute
        || Boolean(window.ReactNativeWebView)
        || window.self !== window.top;
    } catch {
      isEmbedRoute = true;
    }
    const isAuthPage =
      pathname === '/login' ||
      pathname === '/register' ||
      pathname === '/verify-email' ||
      pathname.startsWith('/forgot-password') ||
      pathname.startsWith('/reset-password');

    const errorCode = error.response?.data?.code;

    if (error.response?.status === 403 && !isSigningRoute && !isEmbedRoute) {
      if (errorCode === 'email_verification_required') {
        localStorage.setItem('auth_verification_required', 'true');
        if (pathname !== '/verify-email') {
          window.location.href = '/verify-email';
        }
        return Promise.reject(error);
      }

      if (errorCode === 'account_verification_expired') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_verification_required');
        csrfToken = null;
        tokenExpiry = null;
        if (pathname !== '/register') {
          window.location.href = '/register';
        }
        return Promise.reject(error);
      }
    }

    // Handle 401 unauthorized
    if (error.response?.status === 401 && !isSigningRoute && !isEmbedRoute) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_verification_required');
      csrfToken = null;
      tokenExpiry = null;

      if (!isAuthPage) {
        window.location.href = '/login';
      }
    }
    
    // Handle 419 CSRF token mismatch
    if (error.response?.status === 419) {
      console.error('❌ CSRF token mismatch (419). Details:', {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        headersSent: error.config?.headers,
        responseData: error.response?.data,
        currentToken: csrfToken ? csrfToken.substring(0, 30) + '...' : 'none',
      });
      console.error('Clearing token and retrying...');
      // Clear CSRF token to force re-fetch
      csrfToken = null;
      tokenExpiry = null;
    }
    
    // Log other errors for debugging
    if (error.response) {
      console.error('❌ API Error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        data: error.response.data,
        headers: error.response.headers,
      });
    }
    
    return Promise.reject(error);
  }
);

// Export function to clear CSRF token (useful for logout)
export const clearCsrfToken = () => {
  csrfToken = null;
  tokenExpiry = null;
};

export default api;